/* istanbul ignore file */
import { AxiosError, AxiosRequestConfig } from 'axios'
import {
  AgreementShape,
  MpxResponseBaseSingle,
  PaginationRequest,
  PaginationResponse,
  Persona,
  ProgramConfig,
  ProgramReward,
  RewardToken,
  UserId,
  YearlyProgramReward,
} from '../types'
import { generatePath } from '../utils'
import { FDRStatsQueryItem, FieldFDRStats } from './FieldsAPI'
import { getMpAgent } from './utils'

export interface OrgUserSearchParams {
  fullText?: string
  persona?: Persona | Persona[]
  isActive?: boolean
  fips?: string[]
  acresLt?: number
  acresGt?: number
  orgIds?: string[]
  email?: string
  account?: string
  advisor?: string
  orgId?: string
  fdrStats?: FDRStatsQueryItem[]
}

export interface OrgUserSearchRequest extends PaginationRequest<{}>, OrgUserSearchParams {
  //@deprecated
  owningOrgId?: string
}

export interface UserSearchValues {}

export type OrgUserSearchResultResourceProgram = {
  programId: ProgramConfig['programId']
  eligible: boolean
  // Yep, we decided to perpetuate the awful design from FieldFDRStats here.
  rewards: Record<
    'potential' | 'earned' | 'delivered',
    Record<RewardToken, Pick<ProgramReward, 'amount' | 'token'>>
  >
}

export interface OrgUserSearchResult {
  account: string
  email: string
  familyName?: string
  givenName?: string
  persona?: Persona
  userId: UserId
  resourceId: string
  matches: {
    fullName?: string
    givenName?: string
    familyName?: string
    email?: string
  }
  lastLoginDate?: string
  allTimeEarnings?: any
  advisorId?: UserId
  isActive?: boolean
  hasSentInvite?: boolean
  org?: {
    id: string
    label: string
  }
  resources?: {
    fields: number
    acres: number
    committed: {
      fields: number
      acres: number
    }
    programs: OrgUserSearchResultResourceProgram[]
  }
  agreements?: AgreementShape[]
  advisor?: {
    email: string
    familyName?: string
    givenName?: string
    userId: UserId
  }
  fdrStats?: FieldFDRStats[]

  potentialRewards?: Array<PotentialReward>
  eligiblePrograms?: Array<ProgramConfig['programId']>
}

export type PotentialReward = {
  programId: ProgramConfig['programId']
  rewards: YearlyProgramReward[]
}

export interface OrgUserUpdate {
  email: string
  account?: string
  persona?: Persona
  givenName?: string
  familyName?: string
  owningOrgId?: string
  advisorId?: UserId
  isActive?: boolean
  extraData?: Object
}

export interface OrgUsersInviteRequest {
  userId: UserId
  alwaysSend?: boolean
  userOverrideInviteEmailCode?: string
  suppressNotification?: boolean
}

// any - we ignore the generic item<T>
export interface OrgUsersInviteResponse extends Omit<MpxResponseBaseSingle<any>, 'item'> {
  userId: UserId
  email: string
  hasInviteSent?: boolean
  skipped?: boolean
  reason?: string
  notifiedAdvisor?: boolean
}

export interface OrgUserCreate extends OrgUserUpdate {
  sendInvite?: boolean
}

const agent = getMpAgent()

const PATHS = {
  USER: '/org/users',
  USER_BY_ID: '/org/users/:userId',
  SEARCH: '/org/users/search',
  INVITE: '/org/users/invite',
}

export class OrgUsersAPI {
  static user(userId: UserId, config?: AxiosRequestConfig) {
    return agent
      .get<MpxResponseBaseSingle<OrgUserSearchResult>>(
        generatePath(PATHS.USER_BY_ID, { userId: userId?.toString() }),
        config
      )
      .then(response => response.data)
  }

  static patchUser(payload: OrgUserUpdate, config?: AxiosRequestConfig) {
    return agent
      .patch<MpxResponseBaseSingle<OrgUserSearchResult>>(generatePath(PATHS.USER), payload)
      .then(response => response.data)
  }

  static paginatedUsers(
    { persona, fdrStats, fips, orgIds, ...request }: OrgUserSearchRequest,
    headers?: any
  ) {
    return agent
      .get<PaginationResponse<OrgUserSearchResult>>(PATHS.SEARCH, {
        params: {
          persona: !!persona ? (Array.isArray(persona) ? persona.join(',') : persona) : undefined,
          fips:
            !!fips && fips.length > 0 ? (Array.isArray(fips) ? fips.join(',') : fips) : undefined,
          orgIds: !!orgIds ? (Array.isArray(orgIds) ? orgIds.join(',') : orgIds) : undefined,
          fdrStats: !!fdrStats ? JSON.stringify(fdrStats) : undefined,
          ...request,
        },
        headers,
      })
      .then(response => response.data)
      .catch((error: AxiosError | unknown) => {
        if ((error as AxiosError).isAxiosError) {
          console.error((error as AxiosError).message)
        }
        console.error({ error })
        throw error
      })
  }

  static createUser(payload: OrgUserCreate, config?: AxiosRequestConfig) {
    return agent.post<MpxResponseBaseSingle<OrgUserSearchResult>>(PATHS.USER, payload, config)
  }

  static sendInvite(payload: OrgUsersInviteRequest, config?: AxiosRequestConfig) {
    return agent.post<OrgUsersInviteResponse>(PATHS.INVITE, payload, config)
  }
}
